import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom } from 'jotai';
import { PlusIcon } from '@heroicons/react/24/outline';

import { SkeletonEmployerCardListLoading } from '@restworld/ui-components';
import { Button as ButtonV2, Heading, Link } from '@restworld/ui-ds-base';
import useEmployerJobListings from '../../../../job-listing/hooks/use-employer-job-listing';
import EmployerJobListingCardsList from '../../../../job-listing/employer-job-listing-cards-list';
import { useEmployerRestaurantDetails } from '../../../hooks/use-employer-restuarant-details';
import { showContactRestWorldManagerModalAtom } from '../../../employer-restaurants-filters/employer-restaurants.atoms';
import EmployerNoResults from '../../../../employer-no-results';
import { cn } from '@restworld/utils-common';

function RestaurantOpenJobPositionsSection({ isDashboardView }: { isDashboardView?: boolean }) {
  const { t } = useTranslation();
  const restaurantDetails = useEmployerRestaurantDetails();
  const [, setShowContactRestworldManagerModal] = useAtom(showContactRestWorldManagerModalAtom);

  const queryConfig = useMemo(
    () =>
      isDashboardView
        ? { status: 'online' }
        : {
            restaurantName: restaurantDetails?.restaurant_name,
            status: 'online',
            enabled: !!restaurantDetails,
          },
    [isDashboardView, restaurantDetails]
  );

  const { jobPositionItems, query, hasNextPage, loading } = useEmployerJobListings(queryConfig);

  if (isDashboardView && !query.isLoading && !jobPositionItems?.length) return null;

  return (
    <SectionWrapper>
      <HeaderWrapper>
        <div className="flex items-center gap-3">
          <Heading color="neutral" className={isDashboardView ? 'text-base' : 'text-lg lg:text-xl'}>
            {isDashboardView ? t('dashboard-page:your_job_positions') : t('restaurant-details-page:open_positions')}
          </Heading>
        </div>
        {!isDashboardView && (
          <Link StartIcon={PlusIcon} onClick={() => setShowContactRestworldManagerModal(true)} newTab={false}>
            {t('restaurant-details-page:open_a_new_position')}
          </Link>
        )}
      </HeaderWrapper>
      <CardsWrapper>
        {loading ? (
          <>
            <SkeletonEmployerCardListLoading />
            <SkeletonEmployerCardListLoading />
          </>
        ) : !jobPositionItems.length ? (
          <NoResultsWrapper>
            <EmployerNoResults
              title={t('restaurant-details-page:currently_you_have_no_open_job_positions')}
              content={t('restaurant-details-page:create_a_new_job_posting_so_we_can_send_candidates')}
              contentStyle={'max-w-screen-sm'}
            />
          </NoResultsWrapper>
        ) : (
          <EmployerJobListingCardsList jobPositionItems={jobPositionItems} />
        )}
      </CardsWrapper>
      <LoadMoreBtnWrapper>
        {hasNextPage && (
          <ButtonV2
            StartIcon={PlusIcon}
            variant="tertiary"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className={'mt-16'}
          >
            {t('load_more_items')}
          </ButtonV2>
        )}
      </LoadMoreBtnWrapper>
    </SectionWrapper>
  );
}
export default RestaurantOpenJobPositionsSection;

const SectionWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <section className={cn('mt-16', className)} {...props} />
);
const HeaderWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-between mb-4', className)} {...props} />
);
const CardsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4', className)} {...props} />
);
const LoadMoreBtnWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-center', className)} {...props} />
);
const NoResultsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-2xl border border-neutral-40 py-5 lg:py-8', className)} {...props} />
);
